import {
  useParams,
  useSearchParams,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import DailyGridHeaderSummary from "../components/Report/MoreDetails/DailyGridHeaderSummary";
import DailyGrid from "../components/Report/MoreDetails/DailyGrid";
import DailyGridSkeleton from "../components/Report/MoreDetails/DailyGridSkeleton";
import { useDailyGrid } from "../components/Report/MoreDetails/useDailyGrid";
import ExportButton from "../components/ExportButton";
import { exportSaleReportDailyGrid } from "../services";

// Breadcrumb only — no back chevron (matches the existing ReportDetailsPage:
// "รายงาน / <code>").
const BackBar = ({ code }: { code?: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
    <RouterLink
      to="/report/sale"
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        รายงาน
      </Typography>
    </RouterLink>
    <Typography variant="body1" sx={{ mx: 1 }}>
      /
    </Typography>
    <Typography variant="body1" color="primary">
      {code ?? "รายละเอียดเพิ่มเติม"}
    </Typography>
  </Box>
);

const CenteredState = ({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) => (
  <Paper>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: 8,
        gap: 1.5,
      }}
    >
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
      {action}
    </Box>
  </Paper>
);

export default function SaleReportMoreDetailsPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version");
  const navigate = useNavigate();

  const { status, data, refetch } = useDailyGrid(uuid, version);

  if (status === "loading") {
    return <DailyGridSkeleton />;
  }

  if (status === "error") {
    return (
      <Box sx={{ padding: "8px 0" }}>
        <BackBar />
        <CenteredState
          icon={
            <ReportProblemOutlinedIcon
              sx={{ fontSize: 56, color: "error.main" }}
            />
          }
          title="ไม่สามารถโหลดข้อมูลรายงานได้"
          description="เกิดข้อผิดพลาดในการเรียกข้อมูล กรุณาลองใหม่อีกครั้ง"
          action={
            <Button variant="contained" onClick={refetch} sx={{ mt: 1 }}>
              ลองใหม่อีกครั้ง
            </Button>
          }
        />
      </Box>
    );
  }

  // status === "success"
  if (!data) {
    return (
      <Box sx={{ padding: "8px 0" }}>
        <BackBar />
        <CenteredState
          icon={<InboxOutlinedIcon sx={{ fontSize: 56, color: "#b9b9c6" }} />}
          title="ไม่พบข้อมูลรายงาน"
          description="รายงานนี้อาจถูกลบหรือยังไม่มีข้อมูล"
        />
      </Box>
    );
  }

  const { header, columns, rows, footer } = data;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <BackBar code={header.sales_report_code} />

      <Paper sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
            pb: 1.5,
            borderBottom: (theme) => `1px solid ${theme.palette.draft.main}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                รายงานการขายแบบละเอียด
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Detailed Sale Daily Report
              </Typography>
            </Box>
          </Box>
          {uuid && (
            <ExportButton
              request={() => exportSaleReportDailyGrid(uuid, version)}
              fallbackFilename={`sale-daily-report-detail_${header.route_code}_${header.report_date.replace(/-/g, "")}.xlsx`}
            />
          )}
        </Box>

        <DailyGridHeaderSummary header={header} storeCount={rows.length} />
      </Paper>

      <Paper>
        <DailyGrid
          header={header}
          columns={columns}
          rows={rows}
          footer={footer}
          onBillClick={(row) => {
            if (!row.bill_uuid) return;
            const isInvoice =
              (row.bill_type ?? "").toLowerCase() === "invoice";
            navigate(
              isInvoice
                ? `/sales/invoice/details/${row.bill_uuid}`
                : `/sales/cash-sales/details/${row.bill_uuid}`,
            );
          }}
        />
      </Paper>
    </Box>
  );
}
